/**
 * ClaimSync Sign Up Page
 * Split-screen design with branding panel and registration form
 */

import React from 'react';
import { SignUp, useUser } from '@clerk/clerk-react';
import { Link, Navigate } from 'react-router-dom';
import { Calculator, FileText, BarChart3, Shield, Scale, CheckCircle } from 'lucide-react';

export default function SignUpPage() {
  const { isSignedIn, isLoaded } = useUser();

  // Redirect if already signed in
  if (isLoaded && isSignedIn) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-gradient-to-br from-[#0b2545] via-[#134e6f] to-[#0b2545] p-12 flex-col justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img 
            src="/logo.svg" 
            alt="ClaimSync" 
            className="h-44 lg:h-56 w-auto"
          />
        </div>

        {/* Main Content */}
        <div className="max-w-xl">
          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            Start Calculating Claims Today
          </h1>
          <p className="text-lg text-blue-200 mb-10 leading-relaxed">
            Join thousands of professionals using ClaimSync to streamline their business interruption claim calculations.
          </p>

          {/* Benefits List */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-semibold">BI Calculations Made Easy</h3>
                <p className="text-blue-200 text-sm">Standard Business Interruption Calculator with automated workflows</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-semibold">AI-Powered Analysis</h3>
                <p className="text-blue-200 text-sm">Smart transaction classification and P&L reconstruction</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-semibold">Monte Carlo Risk Analysis</h3>
                <p className="text-blue-200 text-sm">Statistical modeling for claim range projections</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-semibold">Professional PDF Reports</h3>
                <p className="text-blue-200 text-sm">Court-ready documentation with full audit trails</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-blue-300 text-sm">
          © {new Date().getFullYear()} ClaimSync. Professional business interruption claim calculations.
        </div>
      </div>

      {/* Right Panel - Sign Up Form */}
      <div className="w-full lg:w-1/2 xl:w-[45%] flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logo.svg" 
              alt="ClaimSync" 
              className="h-32 w-auto mx-auto mb-4"
            />
          </div>

          {/* Welcome Text */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your Account</h2>
            <p className="text-gray-600">Get started with ClaimSync in minutes</p>
          </div>

          {/* Clerk Sign Up */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-1">
            <SignUp 
              routing="hash"
              signInUrl="/sign-in"
              forceRedirectUrl="/"
              appearance={{
                elements: {
                  rootBox: "w-full",
                  card: "shadow-none border-0 bg-transparent",
                  headerTitle: "hidden",
                  headerSubtitle: "hidden",
                  socialButtonsBlockButton: "bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors",
                  socialButtonsBlockButtonText: "font-medium",
                  dividerLine: "bg-gray-200",
                  dividerText: "text-gray-500 text-sm",
                  formFieldLabel: "text-gray-700 font-medium",
                  formFieldInput: "bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg",
                  formButtonPrimary: "bg-gray-900 hover:bg-gray-800 text-white rounded-lg py-3 font-semibold transition-colors",
                  footerActionLink: "text-blue-600 hover:text-blue-700 font-medium",
                  identityPreviewText: "text-gray-700",
                  identityPreviewEditButton: "text-blue-600",
                  formFieldSuccessText: "text-green-600",
                  formFieldErrorText: "text-red-600",
                  alertText: "text-gray-700",
                  otpCodeFieldInput: "border-gray-200 focus:border-blue-500",
                  formResendCodeLink: "text-blue-600 hover:text-blue-700"
                },
                layout: {
                  socialButtonsPlacement: "bottom",
                  showOptionalFields: false
                }
              }}
            />
          </div>

          {/* Additional Links */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/sign-in" className="text-blue-600 hover:text-blue-700 font-semibold">
                Sign in
              </Link>
            </p>
          </div>

          {/* Security Badge */}
          <div className="mt-8 flex items-center justify-center gap-2 text-gray-400 text-sm">
            <Shield className="w-4 h-4" />
            <span>Secured by Clerk</span>
          </div>
        </div>
      </div>
    </div>
  );
}
